import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface ModGroupOpenApi {
  /** Has the agent explicitly COLLAPSED this group? */
  isClosedByAgent: (pathToItem: string) => boolean;
  toggle: (pathToItem: string) => void;
  /** Clear a collapse, because the group has just been addressed from outside. */
  reopen: (pathToItem: string) => void;
}

const noop: ModGroupOpenApi = {
  isClosedByAgent: () => false,
  toggle: () => undefined,
  reopen: () => undefined,
};

const ModGroupOpenContext = createContext<ModGroupOpenApi>(noop);

/**
 * HITL-1616 — which option drawers the agent has COLLAPSED.
 *
 * ## Open is the default
 *
 * Every drawer starts open, the way the chip grid showed every group's options
 * at once. The agent is on a headset with a customer talking: making them click
 * to see the options trades a glance for an interaction, which is the wrong
 * trade however much vertical space it saves. Collapsing is there for an item
 * long enough to be worth tidying, not as the resting state.
 *
 * So this holds the agent's CLOSURES, and a group is open unless it is in here.
 *
 * ## Why this is component state and not `dialogSlice`
 *
 * Being addressed from outside re-opens a group the agent collapsed, and the
 * three signals that mean "addressed" already live in Redux and already reach
 * `ModGroup`:
 *
 * - `dialogSlice.selectedModGroupPath` matches, which `ModGroup` already
 *   receives as `isSelectedToFocus` — that covers Shift+W/S/A/D
 *   (`useModifierGroupNavigation.hooks.ts:142`) AND clicking a mod group in the
 *   cart (`CartItemModGroups.tsx:140`);
 * - `dialogSlice.highlightedModifier` matches, which `ModGroup` already computes
 *   as `isHighlighted` — that covers the omnibar's modifier search
 *   (`AutocompleteComp.tsx:1109`);
 * - the HITL-1433 auto-reveal targeted it.
 *
 * So every one of the existing "scroll to that group" behaviours re-opens the
 * drawer by construction, with no new action, no new reducer, and nothing for a
 * caller to remember. Adding an open-state field to `dialogSlice` would mean
 * teaching each of those to write it, which is exactly the kind of list that
 * goes stale.
 *
 * Scoped per cart item: the provider sits in `CartItemDetail`, so opening a
 * drawer on one item says nothing about the next item the agent selects.
 */
export const ModGroupOpenProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [closedPaths, setClosedPaths] = useState<Record<string, boolean>>({});

  const isClosedByAgent = useCallback(
    (pathToItem: string) => !!closedPaths[pathToItem],
    [closedPaths]
  );

  const toggle = useCallback((pathToItem: string) => {
    setClosedPaths((current) => ({
      ...current,
      [pathToItem]: !current[pathToItem],
    }));
  }, []);

  /**
   * Being addressed from outside re-opens a group the agent had collapsed — as
   * an EVENT, not as a standing override.
   *
   * Reading `isSelectedToFocus` straight into `isOpen` instead would mean the
   * selected group could never be collapsed at all: the flag stays true for as
   * long as the group is selected, so every collapse would be undone on the
   * next render.
   *
   * Returns the same object when there is nothing to clear, so this cannot loop
   * a render.
   */
  const reopen = useCallback((pathToItem: string) => {
    setClosedPaths((current) =>
      current[pathToItem] ? { ...current, [pathToItem]: false } : current
    );
  }, []);

  const api = useMemo(
    () => ({ isClosedByAgent, toggle, reopen }),
    [isClosedByAgent, toggle, reopen]
  );

  return (
    <ModGroupOpenContext.Provider value={api}>
      {children}
    </ModGroupOpenContext.Provider>
  );
};

export const useModGroupOpen = () => useContext(ModGroupOpenContext);

/**
 * THE rule this whole story turns on: a closed drawer is HIDDEN, never
 * unmounted.
 *
 * `ModGroup.tsx:158-202` applies a group's default modifier selections from a
 * mount effect, and for a nested group revealed after add-time it is the only
 * mechanism that does so — `processDefaultSelectedModifiers` runs once at
 * add-item time and cascades only through groups that already exist. Unmount a
 * closed group and the cart's contents become a function of which rows the agent
 * happened to open; in `Intercept` that difference reaches the POS through
 * `OrderReview.tsx:115`.
 *
 * `ConsoleCard`'s `keepBodyMounted` does the same thing for `OrderReview`, for
 * the same reason.
 */
export const HIDDEN_WHEN_CLOSED: React.CSSProperties = Object.freeze({
  display: 'none',
});

export default ModGroupOpenContext;
